import React, { FC, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { BODY_FONT, g, t, TICKET } from '../../HomeScreen/HomeScreen.styles';
import { useTrackEvent } from '../../../../common/hooks/useTrackEvent';
import { EventNames } from '../../../../common/constants/constants';
import {
  getLocationPermissionState,
  hasDeviceLocation,
  requestDeviceLocation,
} from '../../../../services/analytics/deviceLocation';

/* Optional location ask on the complete screen. The upload is already done,
   so the browser prompt can't cost a video. Shown only when we don't already
   have a location and the site isn't already blocked (a blocked site fails
   without a prompt, so the button would do nothing). */

// Off for now: no location permission is requested anywhere. The silent read
// on arrival (only when the site already has permission) is unaffected.
const LOCATION_ASK_ENABLED = false;

const Container = styled('section')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: `${g(8)} 0`,
});

const Title = styled('h2')({
  fontSize: t(27),
  lineHeight: 1.2,
  letterSpacing: '-0.015em',
  fontWeight: 700,
  fontFamily: BODY_FONT,
  color: TICKET.labelInk,
  margin: 0,
});

const Body = styled('p')({
  fontSize: t(17),
  lineHeight: 1.4,
  fontFamily: BODY_FONT,
  color: TICKET.tagline,
  margin: `${g(10)} 0 0`,
  maxWidth: '30em',
});

const ShareButton = styled('button')(({ theme, disabled }) => ({
  width: '100%',
  height: `max(50px, ${g(90)})`,
  borderRadius: g(14),
  border: 'none',
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: t(26),
  fontWeight: 700,
  fontFamily: BODY_FONT,
  letterSpacing: '-0.01em',
  boxShadow: '0 1.5px 2px rgba(0,0,0,.24), 0 8px 18px rgba(0,0,0,.08)',
  cursor: disabled ? 'default' : 'pointer',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  marginTop: g(18),
  opacity: disabled ? 0.6 : 1,
  transition: 'opacity 160ms ease, transform 160ms ease',
  '&:active': disabled ? {} : { transform: 'scale(0.985)' },
}));

const SkipButton = styled('button')({
  border: 'none',
  background: 'none',
  color: TICKET.statText,
  fontSize: t(17),
  fontWeight: 600,
  fontFamily: BODY_FONT,
  marginTop: g(12),
  padding: `${g(8)} ${g(16)}`,
  minHeight: '44px',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
});

const Result = styled('p')({
  fontSize: t(19),
  fontWeight: 600,
  fontFamily: BODY_FONT,
  color: TICKET.labelInk,
  margin: 0,
  minHeight: '44px',
  display: 'flex',
  alignItems: 'center',
});

/** Whether to show the ask: resolves once the complete screen is up. */
export const useShouldAskLocation = (active: boolean): [boolean, () => void] => {
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    if (!LOCATION_ASK_ENABLED || !active || hasDeviceLocation()) return undefined;
    let cancelled = false;
    getLocationPermissionState().then((state) => {
      if (!cancelled && state !== 'denied') setAsk(true);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);
  return [ask, () => setAsk(false)];
};

type Phase = 'idle' | 'asking' | 'shared' | 'declined';

interface Props {
  onSkip: () => void;
}

export const LocationBlock: FC<Props> = ({ onSkip }) => {
  const { trackEvent } = useTrackEvent();
  const [phase, setPhase] = useState<Phase>('idle');
  const viewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    getLocationPermissionState().then((permission) => trackEvent({ action: EventNames.location_ask_view, permission }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShare = () => {
    if (phase !== 'idle') return;
    trackEvent({ action: EventNames.location_ask_action, choice: 'share' });
    setPhase('asking');
    // Inside the tap: the browser prompt needs the user gesture.
    requestDeviceLocation().then((result) => {
      trackEvent({ action: EventNames.device_location_result, ...result });
      setPhase(result.outcome === 'captured' ? 'shared' : 'declined');
    });
  };

  const handleSkip = () => {
    trackEvent({ action: EventNames.location_ask_action, choice: 'skip' });
    onSkip();
  };

  if (phase === 'shared' || phase === 'declined') {
    return (
      <Container aria-live="polite">
        <Result>{phase === 'shared' ? 'Thanks! Location shared.' : 'No problem, your video is all set.'}</Result>
      </Container>
    );
  }

  return (
    <Container aria-label="Share your location">
      <Title>Where was this filmed?</Title>
      <Body>Share your location to help us see where stories are coming from. It&apos;s optional.</Body>
      <ShareButton type="button" onClick={handleShare} disabled={phase === 'asking'}>
        {phase === 'asking' ? 'Waiting for your answer…' : 'Share location'}
      </ShareButton>
      {phase === 'idle' && (
        <SkipButton type="button" onClick={handleSkip}>
          Not now
        </SkipButton>
      )}
    </Container>
  );
};
